import React, { useEffect, useState } from "react";

import { Typography } from "@packages/ds";
import moment from "moment";
import PropTypes from "prop-types";
import { FormattedMessage, useIntl } from "react-intl";

import ContentLayout from "Components/ContentLayout";
import * as DrawerLayout from "Components/DrawerLayout";
import { BackLink, Link } from "ds/Button";
import useDecodedParams from "Hooks/useDecodedParams";
import { formatAsCurrency } from "Libs/utils";
import { selectFeatureUsageBasedBillingEnabled } from "Reducers/featureFlags/featureFlags.selectors";
import { organizationByDescriptionIdSelector } from "Reducers/organization";
import { useLoadOrders } from "Reducers/organization/order/hooks/useLoadOrders";
import { getOrganizationPrepayment } from "Reducers/organization/prepayment";
import Invoice from "src/organization/pages/billing/components/Invoice/Invoice";
import Layout from "src/organization/pages/billing/components/Layout";
import BillingListField from "src/organization/pages/billing/containers/BillingListField";
import { useAppDispatch, useAppSelector } from "Store/hooks";

import * as S from "./InvoicesPage.styles";
import { InvoicesSkeleton } from "./InvoicesSkeleton";
import PastInvoices from "./PastInvoices";

const Page = ({ titleFilter }) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();

  const { organizationId } = useDecodedParams();

  const [pastInvoicesView, setPastInvoicesView] = useState(false);
  const {
    recurring,
    previous,
    loading: loadingOrders
  } = useLoadOrders(organizationId);

  const recurringOrders = recurring ? Object.values(recurring) : [];
  const previousOrders = previous ? Object.values(previous) : [];

  const [showInvoice, setShowInvoice] = useState(recurringOrders[0]);
  const [invoiceStatus, setInvoiceStatus] = useState(
    recurringOrders[0]?.status
  );

  const upcomingInvoiceCurrency =
    recurringOrders[0]?.components["base_price"].currency;
  const upcomingInvoiceTotal = recurringOrders[0]?.total;

  const usageBasedPagesEnabled = useAppSelector(
    selectFeatureUsageBasedBillingEnabled
  );

  const organization = useAppSelector(state =>
    organizationByDescriptionIdSelector(state, {
      organizationDescriptionId: organizationId
    })
  );

  const prepayment = useAppSelector(state => {
    if (organization) {
      return state.organizationPrepayment.data[organization.id];
    }
  });

  useEffect(() => {
    if (organization) {
      dispatch(getOrganizationPrepayment({ organizationId: organization.id }));
    }
  }, [dispatch, organization]);

  useEffect(() => {
    setShowInvoice(recurringOrders[0]);
  }, [recurringOrders?.length]);

  const handleUpcomingInvoiceView = () => {
    setShowInvoice(recurringOrders[0]);
    setInvoiceStatus(recurringOrders[0]?.status);
    setPastInvoicesView(true);
  };

  const handleShowLatestInvoices = previousInvoice => {
    setInvoiceStatus(previousInvoice.status);
    setShowInvoice(previousInvoice);
  };

  return usageBasedPagesEnabled ? (
    <S.InvoicesPageWrapper>
      <S.DrawerRoot>
        <DrawerLayout.Drawer>
          <DrawerLayout.Block>
            <S.DrawerTitle tag="h2">
              <FormattedMessage id="invoices" />
            </S.DrawerTitle>
            <S.OrganizationName tag="h3">
              {organization?.name}
            </S.OrganizationName>
          </DrawerLayout.Block>

          <DrawerLayout.Block>
            <Typography.Title tag="h3">
              <FormattedMessage id="billing.details.upcoming.invoice" />
            </Typography.Title>
            <S.UpcomingEstimatedPayment>
              <S.UpcomingEstimatedPaymentAmount>
                <Typography.Title tag="h3">
                  {upcomingInvoiceCurrency &&
                    formatAsCurrency(
                      upcomingInvoiceTotal,
                      upcomingInvoiceCurrency
                    )}
                </Typography.Title>
                <S.EstimatedAmountCurrency weight="semiBold" size="extraSmall">
                  {upcomingInvoiceCurrency}
                </S.EstimatedAmountCurrency>
              </S.UpcomingEstimatedPaymentAmount>
              <S.EstimatedPaymentLabel weight="semiBold" size="extraSmall">
                <FormattedMessage id="billing.details.estimated.payment" />
              </S.EstimatedPaymentLabel>
            </S.UpcomingEstimatedPayment>
            {prepayment && prepayment.balance.amount > 0 && (
              <S.RemainingPrepayment>
                <S.UpcomingEstimatedPaymentAmount>
                  <Typography.Title tag="h3">
                    {prepayment.balance.amount > 0 && "+"}
                    {formatAsCurrency(
                      prepayment.balance.amount,
                      prepayment.balance.currency_code
                    )}
                  </Typography.Title>
                  <S.EstimatedAmountCurrency
                    weight="semiBold"
                    size="extraSmall"
                  >
                    {prepayment.balance.currency_code}
                  </S.EstimatedAmountCurrency>
                </S.UpcomingEstimatedPaymentAmount>
                <S.EstimatedPaymentLabel
                  $color="ebony"
                  size="extraSmall"
                  weight="semiBold"
                >
                  <FormattedMessage id="billing.prepaid_funds_remaining" />
                </S.EstimatedPaymentLabel>
              </S.RemainingPrepayment>
            )}
          </DrawerLayout.Block>
          {!pastInvoicesView && (
            <DrawerLayout.Block>
              {previousOrders.length > 0 && (
                <S.PastInvoices>
                  <Typography.Title tag="h3">
                    <FormattedMessage id="billing.details.past.invoices" />
                  </Typography.Title>
                  <Link
                    analyticId="view_all"
                    onClick={() => setPastInvoicesView(true)}
                  >
                    <FormattedMessage id="view_all" />
                  </Link>
                </S.PastInvoices>
              )}
              <S.PastInvoicesShortList>
                {previousOrders
                  ?.sort((a, b) => {
                    if (a.billing_period_end < b.billing_period_end) return 1;
                    if (a.billing_period_end > b.billing_period_end) return -1;
                    return 0;
                  })
                  .slice(0, 5)
                  ?.map(previousInvoice => {
                    return (
                      <S.PastInvoiceRow
                        key={previousInvoice.id}
                        onClick={() =>
                          handleShowLatestInvoices(previousInvoice)
                        }
                      >
                        <S.PastInvoiceId weight="semiBold" size="small">
                          {previousInvoice.id}
                        </S.PastInvoiceId>
                        <S.PastInvoiceInterval
                          weight="semiBold"
                          size="extraSmall"
                        >
                          {`${moment(
                            previousInvoice.billing_period_start
                          ).format("MMM D")}
                            -
                            ${moment(previousInvoice.billing_period_end)
                              .endOf("month")
                              .format("D")}`}
                        </S.PastInvoiceInterval>
                        <S.PastInvoiceAmount weight="semiBold" size="small">
                          {formatAsCurrency(
                            previousInvoice.total,
                            previousInvoice.currency
                          )}
                        </S.PastInvoiceAmount>
                      </S.PastInvoiceRow>
                    );
                  })}
              </S.PastInvoicesShortList>
            </DrawerLayout.Block>
          )}
        </DrawerLayout.Drawer>
      </S.DrawerRoot>
      <S.InvoicesListSection>
        {pastInvoicesView ? (
          loadingOrders ? (
            <InvoicesSkeleton />
          ) : (
            <>
              <S.BackToInovicesLink onClick={() => setPastInvoicesView(false)}>
                <FormattedMessage id="billing.details.back.to.upcoming.invoice" />
              </S.BackToInovicesLink>
              <Typography.Title tag="h2">
                <FormattedMessage id="billing.details.past.invoices" />
              </Typography.Title>
              <PastInvoices
                previousOrders={previousOrders}
                setShowInvoice={setShowInvoice}
                setPastInvoicesView={setPastInvoicesView}
                setInvoiceStatus={setInvoiceStatus}
              />
            </>
          )
        ) : (
          !loadingOrders && (
            <>
              {invoiceStatus === "completed" ? (
                <>
                  <BackLink onClick={() => handleUpcomingInvoiceView()}>
                    <FormattedMessage id="billing.details.back.to.past.invoices" />
                  </BackLink>
                  <S.PastInvoiceTitle tag="h3">
                    <FormattedMessage id="billing.details.past.invoice" />
                  </S.PastInvoiceTitle>
                </>
              ) : (
                <S.UpcomingInvoiceHeader>
                  <S.UpcomingInvoiceTitle tag="h3">
                    <FormattedMessage id="billing.details.upcoming.invoice" />
                  </S.UpcomingInvoiceTitle>
                  <S.UpcomingInvoiceDescription>
                    <FormattedMessage
                      id="billing.details.upcoming.invoice.description"
                      values={{
                        pricingPage: (
                          <S.PricingPageLink
                            analyticId="pricing.page"
                            href={intl.formatMessage({ id: "links.pricing" })}
                          >
                            <FormattedMessage id="pricing.page" />
                          </S.PricingPageLink>
                        )
                      }}
                    />
                  </S.UpcomingInvoiceDescription>
                </S.UpcomingInvoiceHeader>
              )}

              {showInvoice && (
                <Invoice
                  organizationId={organizationId}
                  orderId={showInvoice?.id}
                />
              )}
            </>
          )
        )}
      </S.InvoicesListSection>
    </S.InvoicesPageWrapper>
  ) : (
    <Layout>
      <ContentLayout id="settings-billing" className="settings-content">
        <S.InvoicedHeader tag="h2">
          <FormattedMessage id="invoices" />
        </S.InvoicedHeader>
        <BillingListField
          organizationId={organizationId}
          titleFilter={titleFilter}
        />
      </ContentLayout>
    </Layout>
  );
};

Page.propTypes = {
  titleFilter: PropTypes.string
};

export default Page;
